// 화면 18 — BandHistoryDrawer 상단 캔들 차트 (lightweight-charts v5).
// 권위: ARCH_DASHBOARD §4.18, 사용자 결정 (2026-05-23):
//   - SVG → lightweight-charts 교체.
//   - 좌측 viewport 잠금 (fixLeftEdge) + 줌/스크롤 기반 lazy-load (옵션 C).
//
// 한국 컨벤션:
//   - 상승 (close >= open): 빨강 (var(--mark-up) → #ef4444 계열)
//   - 하락 (close <  open): 파랑 (var(--mark-down) → #3b82f6 계열)
//
// 구성:
//   - 메인 panel: candlestick + volume overlay (하단 22%)
//   - crosshair: Normal mode + subscribeCrosshairMove 로 상단 오버레이 OHLCV 표시
//   - 인터랙티브 줌/팬 (라이브러리 기본 제공)
//   - 좌측 잠금 (fixLeftEdge): 데이터 좌측 끝 너머로 viewport 이동 차단 — cap 도달 후 빈 공간 방지
//   - 줌/팬 lazy-load: param.from < 5 일 때 onRequestMore(currentDays) 호출 (부모가 effectiveDays 증가)
//
// React 통합 원칙:
//   - 마운트 시 createChart 1회. 언마운트 시 chart.remove() 정확히 1회.
//   - rows / interval 변경 → series.setData() 만 호출 (chart 재생성 X).
//   - ResizeObserver 로 container 너비 추적 → chart.applyOptions({ width }).
//   - drawer close → reopen 반복 시 cleanup 보장 (disposed flag 로 race 방어).
//
// scroll behavior 차등 처리 (rows 변경 reason 별):
//   - 'chip-change' / 'interval-change': setData + scrollToPosition(0) → 우측 정렬 (최근 일자).
//   - 'lazy-load': setData 만 → viewport 유지 (사용자가 보고 있던 좌측 부분 그대로).

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type CandlestickData,
  type HistogramData,
  type LogicalRange,
  type Time,
} from 'lightweight-charts';
import type { OhlcvInterval, OhlcvRow } from './types';

interface Props {
  rows: OhlcvRow[];
  interval: OhlcvInterval;
  /** 현재 fetch 된 데이터의 effectiveDays (lazy-load 트리거 비교용). */
  effectiveDays: number;
  /** 우측 정렬 복귀 여부: true 면 chip/interval 변경 ─ scrollToPosition(0). false 면 viewport 유지 (lazy-load). */
  resetViewport: boolean;
  /** 좌측 viewport 근접 시 더 큰 days 로 fetch 요청. cap 도달 시 부모가 무시. */
  onRequestMore?: (currentEffectiveDays: number) => void;
}

interface HoverState {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

// 한국 컨벤션 색상 (design token 미해석 환경에서도 안전한 hex 직접 사용).
// CSS var 는 lightweight-charts 가 직접 인식하지 못함 (canvas) — getComputedStyle 우회는 과대.
const COLOR_UP = '#ef4444'; // var(--mark-up)
const COLOR_DOWN = '#3b82f6'; // var(--mark-down)
const COLOR_UP_VOL = 'rgba(239, 68, 68, 0.55)';
const COLOR_DOWN_VOL = 'rgba(59, 130, 246, 0.55)';
const COLOR_GRID = 'rgba(0, 0, 0, 0.06)';
const COLOR_BORDER = 'rgba(0, 0, 0, 0.15)';
const COLOR_TEXT = '#666';

// lazy-load 트리거 임계 — viewport 좌측 끝에서 이 캔들 수 이내일 때 fetch 요청.
const LAZY_LOAD_THRESHOLD = 5;
// visibleLogicalRangeChange 이벤트 debounce (ms) — 줌 한 번에 수십 번 발생 방지.
const LAZY_LOAD_DEBOUNCE_MS = 250;

export const CandleChart = memo(function CandleChart({
  rows,
  interval,
  effectiveDays,
  resetViewport,
  onRequestMore,
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<'Histogram'> | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);

  // lazy-load handler 가 최신 props 를 보도록 ref 동기화 (subscribe 는 마운트 시 1회만).
  const effectiveDaysRef = useRef(effectiveDays);
  const onRequestMoreRef = useRef(onRequestMore);
  useEffect(() => {
    effectiveDaysRef.current = effectiveDays;
  }, [effectiveDays]);
  useEffect(() => {
    onRequestMoreRef.current = onRequestMore;
  }, [onRequestMore]);

  // ── 마운트: chart 생성 + series 등록. 언마운트: remove(). ─────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let disposed = false;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: 320,
      layout: {
        background: { color: 'transparent' },
        textColor: COLOR_TEXT,
        fontSize: 11,
        fontFamily: 'inherit',
      },
      grid: {
        vertLines: { color: COLOR_GRID },
        horzLines: { color: COLOR_GRID },
      },
      rightPriceScale: {
        borderColor: COLOR_BORDER,
        scaleMargins: { top: 0.08, bottom: 0.28 },
      },
      timeScale: {
        borderColor: COLOR_BORDER,
        timeVisible: false,
        secondsVisible: false,
        // 최근 일자 우측 고정 — 사용자 스크롤/줌 시에도 마지막 캔들이 viewport 우측 끝.
        rightOffset: 0,
        fixRightEdge: true,
        // 좌측 잠금 — 데이터 좌측 끝 너머로 viewport 이동 차단 (cap 도달 후 빈 공간 방지).
        fixLeftEdge: true,
        lockVisibleTimeRangeOnResize: true,
        shiftVisibleRangeOnNewBar: true,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      localization: {
        locale: 'ko-KR',
        dateFormat: 'yyyy-MM-dd',
      },
      autoSize: false,
    });
    chartRef.current = chart;

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: COLOR_UP,
      downColor: COLOR_DOWN,
      borderUpColor: COLOR_UP,
      borderDownColor: COLOR_DOWN,
      wickUpColor: COLOR_UP,
      wickDownColor: COLOR_DOWN,
      priceFormat: {
        type: 'price',
        precision: 0,
        minMove: 1,
      },
    });
    candleSeriesRef.current = candleSeries;

    // 거래량 overlay — 별도 priceScaleId 로 panel 분리. 하단 22% 차지.
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      color: COLOR_UP_VOL,
    });
    chart.priceScale('volume').applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
    });
    volumeSeriesRef.current = volumeSeries;

    // hover OHLCV 오버레이.
    const onCrosshair = chart.subscribeCrosshairMove.bind(chart);
    const handler = (param: Parameters<Parameters<typeof onCrosshair>[0]>[0]): void => {
      if (disposed) return;
      if (!param.time || !param.point) {
        setHover(null);
        return;
      }
      const candle = param.seriesData.get(candleSeries) as
        | CandlestickData<Time>
        | undefined;
      const vol = param.seriesData.get(volumeSeries) as
        | HistogramData<Time>
        | undefined;
      if (!candle) {
        setHover(null);
        return;
      }
      setHover({
        date: formatTime(param.time),
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: vol?.value ?? 0,
      });
    };
    chart.subscribeCrosshairMove(handler);

    // lazy-load: visibleLogicalRangeChange 구독 (debounced).
    // param.from < THRESHOLD → 좌측 끝 근접 → 부모에 effectiveDays 증가 요청.
    let lazyTimer: ReturnType<typeof setTimeout> | null = null;
    const rangeHandler = (param: LogicalRange | null): void => {
      if (disposed) return;
      if (lazyTimer != null) clearTimeout(lazyTimer);
      lazyTimer = setTimeout(() => {
        if (disposed) return;
        if (param == null) return;
        if (param.from >= LAZY_LOAD_THRESHOLD) return;
        const cb = onRequestMoreRef.current;
        if (!cb) return;
        cb(effectiveDaysRef.current);
      }, LAZY_LOAD_DEBOUNCE_MS);
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(rangeHandler);

    // ResizeObserver — container 너비 변화 시 chart 너비만 갱신.
    const ro = new ResizeObserver((entries) => {
      if (disposed) return;
      const entry = entries[0];
      if (!entry) return;
      const w = entry.contentRect.width;
      if (w > 0) chart.applyOptions({ width: Math.floor(w) });
    });
    ro.observe(container);

    return () => {
      disposed = true;
      if (lazyTimer != null) clearTimeout(lazyTimer);
      ro.disconnect();
      try {
        chart.unsubscribeCrosshairMove(handler);
      } catch {
        // chart 이미 dispose 된 경우 무시.
      }
      try {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(rangeHandler);
      } catch {
        // double-cleanup race 방어.
      }
      try {
        chart.remove();
      } catch {
        // double-cleanup race 방어.
      }
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  // ── rows / interval 변화: setData 만 호출 (chart 재생성 X). ─────
  const candleData = useMemo<CandlestickData<Time>[]>(() => {
    const sorted = [...rows].sort((a, b) =>
      a.trading_date.localeCompare(b.trading_date)
    );
    return sorted.map((r) => ({
      time: r.trading_date as Time,
      open: r.open,
      high: r.high,
      low: r.low,
      close: r.close,
    }));
  }, [rows]);

  const volumeData = useMemo<HistogramData<Time>[]>(() => {
    const sorted = [...rows].sort((a, b) =>
      a.trading_date.localeCompare(b.trading_date)
    );
    return sorted.map((r) => ({
      time: r.trading_date as Time,
      value: r.volume,
      color: r.close >= r.open ? COLOR_UP_VOL : COLOR_DOWN_VOL,
    }));
  }, [rows]);

  useEffect(() => {
    const candle = candleSeriesRef.current;
    const vol = volumeSeriesRef.current;
    const chart = chartRef.current;
    if (!candle || !vol || !chart) return;
    candle.setData(candleData);
    vol.setData(volumeData);
    // resetViewport=true (chip/interval 변경): 우측 정렬 복귀.
    // resetViewport=false (lazy-load 결과 도착): viewport 유지 — fixLeftEdge 가 좌측을
    //   새 데이터 끝까지 자동 확장해주므로 추가 조작 불필요.
    if (resetViewport) {
      const ts = chart.timeScale();
      ts.fitContent();
      ts.scrollToPosition(0, false);
    }
  }, [candleData, volumeData, resetViewport]);

  // interval 변경 시 hover 초기화 (이전 데이터 잔상 방지).
  useEffect(() => {
    setHover(null);
  }, [interval]);

  return (
    <div className="lst-candle">
      <div
        ref={containerRef}
        className="lst-candle__chart"
        role="img"
        aria-label={`${intervalLabel(interval)} 캔들 차트`}
      />
      {hover && (
        <div className="lst-candle__tooltip" role="status" aria-live="polite">
          <span className="lst-candle__tooltip-date">{hover.date}</span>
          <span>
            O <strong>{formatPrice(hover.open)}</strong>
          </span>
          <span>
            H <strong>{formatPrice(hover.high)}</strong>
          </span>
          <span>
            L <strong>{formatPrice(hover.low)}</strong>
          </span>
          <span>
            C <strong>{formatPrice(hover.close)}</strong>
          </span>
          <span className="lst-candle__tooltip-vol">
            V <strong>{formatVolume(hover.volume)}</strong>
          </span>
        </div>
      )}
    </div>
  );
});

function intervalLabel(interval: OhlcvInterval): string {
  switch (interval) {
    case 'daily':
      return '일봉';
    case 'weekly':
      return '주봉';
    case 'monthly':
      return '월봉';
  }
}

// Time(string|number|BusinessDay) → 표시용 YYYY-MM-DD 문자열.
function formatTime(t: Time): string {
  if (typeof t === 'string') return t;
  if (typeof t === 'number') {
    const d = new Date(t * 1000);
    return d.toISOString().slice(0, 10);
  }
  // BusinessDay { year, month, day }
  const y = String(t.year).padStart(4, '0');
  const m = String(t.month).padStart(2, '0');
  const d = String(t.day).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatPrice(v: number): string {
  if (!Number.isFinite(v)) return '-';
  return Math.round(v).toLocaleString('ko-KR');
}

function formatVolume(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '-';
  if (v >= 1e8) return `${(v / 1e8).toFixed(1)}억`;
  if (v >= 1e4) return `${(v / 1e4).toFixed(0)}만`;
  return v.toLocaleString('ko-KR');
}
